/**
 * Tournament Bracket Generation – Serpentine Seeding
 *
 * Ensures same-group teams are placed on opposite sides of the bracket so they
 * can only meet as late as possible (ideally the Final).
 *
 * Algorithm:
 *  1. Serpentine order – group qualifiers by position (all 1st places, all 2nd, …),
 *     alternating forward/reverse group order each round.
 *     e.g. 4 groups, 2 qualifiers → [A1, B1, C1, D1, D2, C2, B2, A2]
 *  2. Spread slot order – place teams into bracket slots using a recursive interleaving
 *     pattern that maximises separation between adjacent serpentine teams.
 *     e.g. 8 slots → [0, 4, 2, 6, 1, 5, 3, 7]
 *  3. First-round matches are consecutive slot pairs: (slot 0 vs slot 1), (slot 2 vs slot 3)…
 *
 * This works for any power-of-2 bracket size regardless of group/qualifier count.
 */

/**
 * Bracket slot order for `size` teams.
 * Ensures teams placed into this order are maximally spread across the bracket tree.
 *
 *   getBracketSlotOrder(2)  → [0, 1]
 *   getBracketSlotOrder(4)  → [0, 2, 1, 3]
 *   getBracketSlotOrder(8)  → [0, 4, 2, 6, 1, 5, 3, 7]
 *   getBracketSlotOrder(16) → [0, 8, 4, 12, 2, 10, 6, 14, 1, 9, 5, 13, 3, 11, 7, 15]
 */
const getBracketSlotOrder = (size) => {
  if (size === 2) return [0, 1];
  const prev = getBracketSlotOrder(size / 2);
  const topSlots = prev.map((s) => s * 2);
  const bottomSlots = topSlots.map((s) => s + 1);
  return [...topSlots, ...bottomSlots];
};

/**
 * Order teams using serpentine seeding across groups.
 *
 * - Round 1 qualifiers (1st place): groups in alphabetical order  A1, B1, C1, D1
 * - Round 2 qualifiers (2nd place): groups in reverse order        D2, C2, B2, A2
 * - Round 3 qualifiers (3rd place): groups in alphabetical order   A3, B3, C3, D3
 * - …
 *
 * Teams must have { groupName, qualRank } where qualRank = 1 for the top qualifier
 * from that group, 2 for the second, etc.
 */
const getSerpentineOrder = (teams) => {
  // Collect unique groups in sorted order
  const groups = [...new Set(teams.map((t) => t.groupName || ''))].sort();

  // Bucket teams by group, sorted by qualRank ascending
  const byGroup = {};
  for (const g of groups) {
    byGroup[g] = teams
      .filter((t) => (t.groupName || '') === g)
      .sort((a, b) => (a.qualRank ?? 0) - (b.qualRank ?? 0));
  }

  const maxRounds = Math.max(...Object.values(byGroup).map((g) => g.length));
  const serpentine = [];

  for (let round = 0; round < maxRounds; round++) {
    const orderedGroups = round % 2 === 1 ? [...groups].reverse() : groups;
    for (const g of orderedGroups) {
      if (byGroup[g][round]) serpentine.push(byGroup[g][round]);
    }
  }

  return serpentine;
};

const getFirstRound = (teamCount) => {
  if (teamCount === 2) return 'final';
  if (teamCount === 4) return 'semi_final';
  if (teamCount === 8) return 'quarter_final';
  if (teamCount === 16) return 'round_of_16';
  throw new Error(`Unsupported bracket size: ${teamCount} teams`);
};

const getRoundPrefix = (round) => {
  switch (round) {
    case 'round_of_16': return 'R16';
    case 'quarter_final': return 'QF';
    case 'semi_final': return 'SF';
    default: return '';
  }
};

const getNextRound = (round) => {
  switch (round) {
    case 'round_of_16': return 'quarter_final';
    case 'quarter_final': return 'semi_final';
    case 'semi_final': return 'final';
    default: return null;
  }
};

/**
 * Check if a number is a power of 2
 */
const isPowerOfTwo = (n) => n > 0 && (n & (n - 1)) === 0;

/**
 * Generate all knockout bracket matches using serpentine seeding.
 *
 * Teams should have: { teamId, seedLabel, groupName, qualRank }
 *   - qualRank: 1 = top qualifier from that group, 2 = second qualifier, etc.
 *
 * @param {Array<{teamId: string, seedLabel: string, groupName: string, qualRank: number}>} qualifiedTeams
 * @param {boolean} [includeBronze=false] - If true, adds a 3rd place match between SF losers
 * @returns {Array} Match objects ready for DB insertion
 */
export const generateKnockoutBracket = (qualifiedTeams, includeBronze = false) => {
  const teamCount = qualifiedTeams.length;

  if (!isPowerOfTwo(teamCount)) {
    throw new Error(`Bracket size must be a power of 2 (2, 4, 8, 16…). Got ${teamCount} teams.`);
  }

  // 1. Order teams using serpentine seeding
  const ordered = getSerpentineOrder(qualifiedTeams);

  // 2. Get spread slot order and assign teams to slots
  const slotOrder = getBracketSlotOrder(teamCount);
  const slotToTeam = new Array(teamCount);
  ordered.forEach((team, i) => {
    slotToTeam[slotOrder[i]] = team;
  });

  const matches = [];
  const firstRound = getFirstRound(teamCount);

  // 3. Final-only bracket (2 teams)
  if (firstRound === 'final') {
    return [{
      team1Id: slotToTeam[0].teamId,
      team2Id: slotToTeam[1].teamId,
      round: 'final',
      matchOrder: 1,
      bracket_position1: slotToTeam[0].seedLabel,
      bracket_position2: slotToTeam[1].seedLabel,
    }];
  }

  // 4. First-round matches (consecutive slot pairs)
  const firstRoundMatchCount = teamCount / 2;
  for (let i = 0; i < firstRoundMatchCount; i++) {
    const t1 = slotToTeam[i * 2];
    const t2 = slotToTeam[i * 2 + 1];
    matches.push({
      team1Id: t1.teamId,
      team2Id: t2.teamId,
      round: firstRound,
      matchOrder: i + 1,
      bracket_position1: t1.seedLabel,
      bracket_position2: t2.seedLabel,
    });
  }

  // 5. Subsequent rounds (empty placeholders filled when prior round completes)
  let currentRound = firstRound;
  let matchesInRound = firstRoundMatchCount;

  while (currentRound !== 'final') {
    const nextRound = getNextRound(currentRound);
    const currentPrefix = getRoundPrefix(currentRound);
    matchesInRound = matchesInRound / 2;

    if (nextRound === 'final') {
      matches.push({
        team1Id: null,
        team2Id: null,
        round: 'final',
        matchOrder: 1,
        bracket_position1: 'SF1_winner',
        bracket_position2: 'SF2_winner',
      });
    } else {
      for (let i = 0; i < matchesInRound; i++) {
        matches.push({
          team1Id: null,
          team2Id: null,
          round: nextRound,
          matchOrder: i + 1,
          bracket_position1: `${currentPrefix}${2 * i + 1}_winner`,
          bracket_position2: `${currentPrefix}${2 * i + 2}_winner`,
        });
      }
    }

    currentRound = nextRound;
  }

  // 3rd place bronze match (SF losers) – only when there are semi-finals
  if (includeBronze && teamCount >= 4) {
    matches.push({
      team1Id: null,
      team2Id: null,
      round: 'bronze',
      matchOrder: 1,
      bracket_position1: 'SF1_loser',
      bracket_position2: 'SF2_loser',
    });
  }

  return matches;
};

/**
 * Advance winner to next round
 * Updates the next match in the bracket with the winner
 *
 * @param {Array} allMatches - All bracket matches
 * @param {string} completedMatchId - ID of completed match
 * @param {string} winnerTeamId - ID of winning team
 * @returns {Object|null} Next match to update, or null if final
 */
export const advanceWinnerInBracket = (allMatches, completedMatchId, winnerTeamId) => {
  const completedMatch = allMatches.find((m) => m.id === completedMatchId);
  if (!completedMatch) return null;

  const { round, matchOrder } = completedMatch;

  let nextRound, nextMatchOrder, teamPosition;

  if (round === 'round_of_16') {
    nextRound = 'quarter_final';
    nextMatchOrder = Math.ceil(matchOrder / 2);
    teamPosition = matchOrder % 2 === 1 ? 'team1' : 'team2';
  } else if (round === 'quarter_final') {
    nextRound = 'semi_final';
    nextMatchOrder = Math.ceil(matchOrder / 2);
    teamPosition = matchOrder % 2 === 1 ? 'team1' : 'team2';
  } else if (round === 'semi_final') {
    nextRound = 'final';
    nextMatchOrder = 1;
    teamPosition = matchOrder === 1 ? 'team1' : 'team2';
  } else {
    return null;
  }

  const nextMatch = allMatches.find(
    (m) => m.round === nextRound && m.matchOrder === nextMatchOrder
  );

  if (!nextMatch) return null;

  return { matchId: nextMatch.id, teamPosition, winnerTeamId };
};

/**
 * Validate qualified teams for bracket generation
 */
export const validateQualifiedTeams = (qualifiedTeams) => {
  if (!Array.isArray(qualifiedTeams) || qualifiedTeams.length === 0) {
    return { valid: false, error: 'No qualified teams provided' };
  }

  if (!isPowerOfTwo(qualifiedTeams.length)) {
    return {
      valid: false,
      error: `Bracket size must be a power of 2 (2, 4, 8, 16…). Got ${qualifiedTeams.length} teams.`,
    };
  }

  for (const team of qualifiedTeams) {
    if (!team.teamId || !team.seedLabel) {
      return { valid: false, error: 'All teams must have teamId and seedLabel' };
    }
  }

  return { valid: true };
};
